import { ESTABELECIMENTO_FALLBACK_BASE64 } from '@/assets/fallback/estabBase64';
import { GenericSelect } from '@/components/GenericSelect';
import BaseLayout from '@/layout/BaseLayout';
import { trpc, trpcClient } from '@/trpcClient';
import { logger } from '@/utils/logger';
import { usePreventLeave } from '@/utils/usePreventLeave';
import { checkFacilityCreateInput, type CreateFacilityInputType } from '@shelflix/schemas/facility/create';
import type { UserDto } from '@shelflix/trpc/routes/user/list/useCase';
import Dropzone from '@shelflix/ui-web/components/Dropzone/index';
import Icon from '@shelflix/ui-web/components/Icon';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  scrollToError,
} from '@shelflix/ui-web/components/ui/form';
import { Input } from '@shelflix/ui-web/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shelflix/ui-web/components/ui/select';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCepAutoFill, useFacilityFormStore } from './store';

/**
 * Convert a File to base64 string (without data URL prefix)
 */
async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// const ESTABELECIMENTO_FALLBACK_URL = '/src/assets/fallback/estabelecimento.jpeg';

export function FacilityCreate() {
  const [isUploading, setIsUploading] = useState(false);

  const { formData, resetForm } = useFacilityFormStore();
  const createFacilityMutation = useMutation(trpc.facility.create.mutationOptions());
  const uploadImageMutation = useMutation(trpc.image.uploadCompressed.mutationOptions());
  usePreventLeave(isUploading);

  const dropzoneRef = useRef<HTMLDivElement>(null);
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<
    CreateFacilityInputType & {
      responsibleUser: {
        uuid: string;
        name: string;
      };
    }
  >({
    defaultValues: {
      name: formData?.name ?? '',
      cnpj: formData?.cnpj ?? '',
      zipCode: formData?.zipCode ?? '',
      street: formData?.street ?? '',
      neighborhood: formData?.neighborhood ?? '',
      number: formData?.number ?? '',
      city: formData?.city ?? '',
      state: formData?.state ?? '',
      responsibleUser: formData?.responsibleUser ?? null,
    },
    mode: 'all',
  });
  useCepAutoFill(form);

  const onSubmit = async (data: CreateFacilityInputType) => {
    if (createFacilityMutation.isPending || uploadImageMutation.isPending) return;
    setIsSubmitting(true);

    try {
      setIsUploading(true);

      // First, upload the image with compression
      const fileToUpload = thumbnail || (await convertFallbackToFile());

      const base64Data = await fileToBase64(fileToUpload);
      const uploadResult = await uploadImageMutation.mutateAsync({
        base64Data,
        filename: fileToUpload.name,
        imageType: 'facility-logo',
      });
      const imageKey = uploadResult.key;
      logger.info('Image uploaded', {
        originalSize: uploadResult.originalSize,
        compressedSize: uploadResult.compressedSize,
        savingsPercent: uploadResult.savingsPercent,
      });

      const payload = {
        ...data,
        imageKey, // Use the pre-uploaded key
      } as CreateFacilityInputType;

      await createFacilityMutation.mutateAsync(payload);
      resetForm();
      navigate('/facility', { state: { toastSuccess: true } });
    } catch (error) {
      toast.error(`Erro ao cadastrar estabelecimento: ${(error as Error).message}`);
    } finally {
      setIsUploading(false);
      setIsSubmitting(false);
    }
  };

  // Carregar imagem de fallback como File inicial
  useEffect(() => {
    const loadFallbackImage = async () => {
      try {
        const fallbackFile = await convertFallbackToFile();
        setThumbnail(fallbackFile);
        form.setValue('fileType', fallbackFile.type);
      } catch (_error) {
        // Silenciar erro de carregamento da imagem de fallback
      }
    };

    loadFallbackImage();
  }, []);

  // Converter imagem de fallback para File quando necessário
  const convertFallbackToFile = async (): Promise<File> => {
    const response = await fetch(ESTABELECIMENTO_FALLBACK_BASE64);
    const blob = await response.blob();
    return new File([blob], 'estabelecimento-fallback.jpeg', { type: 'image/jpeg' });
  };

  return (
    <BaseLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, scrollToError)} className="w-full">
          <div className="flex w-full flex-col gap-4">
            <section className="w-full overflow-hidden rounded-[10px]">
              <Card className="w-full rounded-[10px]">
                <CardHeader className="pb-0">
                  <CardTitle className="text-primary text-[18px] leading-5 font-semibold sm:text-2xl">
                    Novo Estabelecimento
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex w-full flex-col items-center gap-[28px] pt-4 sm:flex-row sm:flex-wrap">
                  <div className="aspect-video max-w-[300px] overflow-hidden bg-transparent">
                    <FormField
                      control={form.control}
                      name="fileType"
                      rules={{ validate: (value) => checkFacilityCreateInput('fileType', value) }}
                      render={({ field }) => (
                        <FormItem className="flex flex-col">
                          <FormControl>
                            <Dropzone
                              onFilesSelected={(files) => {
                                setThumbnail(files[0]);
                                field.onChange(files[0]?.type);
                              }}
                              variant="video"
                              requiredAspectRatio="16:9"
                              ref={dropzoneRef}
                              title="Arraste ou clique aqui para selecionar seu arquivo"
                              description="JPG, PNG ou SVG com fundo transparente (1:1)"
                              value={thumbnail ? [thumbnail] : []}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex w-full flex-col gap-5">
                    <div className="flex w-full flex-col gap-4 sm:flex-row">
                      <div className="sm:w-2/3">
                        <FormField
                          control={form.control}
                          name="name"
                          rules={{ validate: (value) => checkFacilityCreateInput('name', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Nome do Estabelecimento
                              </FormLabel>
                              <FormControl>
                                <Input className="h-10" placeholder="Digite o nome do estabelecimento" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="cnpj"
                          rules={{ validate: (value) => checkFacilityCreateInput('cnpj', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">CNPJ</FormLabel>
                              <FormControl>
                                <Input
                                  mask="99.999.999/9999-99"
                                  className="h-10"
                                  placeholder="00.000.000/0000-00"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="flex w-full flex-col gap-4 sm:flex-row">
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="cep"
                          rules={{ validate: (value) => checkFacilityCreateInput('cep', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">CEP</FormLabel>
                              <FormControl>
                                <Input mask="99999-999" className="h-10" placeholder="00000-000" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="address"
                          rules={{ validate: (value) => checkFacilityCreateInput('address', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Logradouro
                              </FormLabel>
                              <FormControl>
                                <Input className="h-10" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:w-1/3">
                        <FormField
                          control={form.control}
                          name="district"
                          rules={{ validate: (value) => checkFacilityCreateInput('district', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Bairro</FormLabel>
                              <FormControl>
                                <Input className="h-10" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="flex w-full flex-col gap-4 sm:flex-row">
                      <div className="sm:basis-1/6">
                        <FormField
                          control={form.control}
                          name="number"
                          rules={{ validate: (value) => checkFacilityCreateInput('number', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Número</FormLabel>
                              <FormControl>
                                <Input className="h-10" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:basis-1/3">
                        <FormField
                          control={form.control}
                          name="city"
                          rules={{ validate: (value) => checkFacilityCreateInput('city', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">Cidade</FormLabel>
                              <FormControl>
                                <Input className="h-10" {...field} disabled />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="grow-1 sm:basis-1/6">
                        <FormField
                          control={form.control}
                          name="state"
                          rules={{ validate: (value) => checkFacilityCreateInput('state', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">UF</FormLabel>
                              <Select value={field.value} onValueChange={field.onChange} disabled>
                                <FormControl>
                                  <SelectTrigger className="flex !h-10 w-full items-center rounded-md border px-3 text-sm">
                                    <SelectValue placeholder="Selecione a UF" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className=" ">
                                  {[
                                    'AC',
                                    'AL',
                                    'AP',
                                    'AM',
                                    'BA',
                                    'CE',
                                    'DF',
                                    'ES',
                                    'GO',
                                    'MA',
                                    'MT',
                                    'MS',
                                    'MG',
                                    'PA',
                                    'PB',
                                    'PR',
                                    'PE',
                                    'PI',
                                    'RJ',
                                    'RN',
                                    'RS',
                                    'RO',
                                    'RR',
                                    'SC',
                                    'SP',
                                    'SE',
                                    'TO',
                                  ].map((uf) => (
                                    <SelectItem key={uf} value={uf}>
                                      {uf}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="sm:basis-1/3">
                        <FormField
                          control={form.control}
                          name="complement"
                          rules={{ validate: (value) => checkFacilityCreateInput('complement', value) }}
                          render={({ field }) => (
                            <FormItem className="flex flex-col gap-[3px]">
                              <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                                Complemento
                              </FormLabel>
                              <FormControl>
                                <Input className="h-10" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>

                    <div className="sm:w-3/4">
                      <FormField
                        control={form.control}
                        name="referenceLocation"
                        rules={{ validate: (value) => checkFacilityCreateInput('referenceLocation', value) }}
                        render={({ field }) => (
                          <FormItem className="flex flex-col gap-[3px]">
                            <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                              Ponto de Referência
                            </FormLabel>
                            <FormControl>
                              <Input className="h-10" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </section>

            <Card className="rounded-[5px]">
              <CardContent>
                <div className="mb-6">
                  <CardTitle className="text-primary mb-2 [font-family:'Poppins'] text-[18px] leading-5 font-semibold sm:text-2xl">
                    Promotor responsável
                  </CardTitle>
                  <p className="[font-family:'Figtree'] text-sm leading-5 text-[#2d2d2d]">
                    Selecione um usuário promotor, para ser responsável pelo estabelecimento, através do nome ou e-mail.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="responsibleUser"
                  rules={{ validate: (value) => checkFacilityCreateInput('responsibleUser', value?.uuid ?? value) }}
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-[3px] sm:w-[391px]">
                      <FormLabel className="text-xs font-medium text-[var(--gray-primary)]">
                        Pesquisar usuário
                      </FormLabel>
                      <FormControl>
                        <div className="flex flex-row items-center gap-2">
                          <GenericSelect<UserDto>
                            onSelect={(user) => field.onChange(user.uuid ?? user)}
                            value={field.value}
                            fetchItems={async (search) => {
                              if (!search) return [];

                              // Fetch directly via trpcClient to avoid stale data from useQuery cache
                              const result = await trpcClient.user.list.query({
                                search: {
                                  fields: ['name', 'email'],
                                  value: search,
                                },
                                filter: [
                                  { name: 'hierarchy', value: undefined, condition: 'min' },
                                  { name: 'active', condition: 'equals', value: true },
                                ],
                              });

                              return result.users ?? [];
                            }}
                            renderItem={(user) => (
                              <div>
                                <div className="text-sm font-medium">{user.name}</div>
                                <div className="text-muted-foreground text-xs">{user.email}</div>
                              </div>
                            )}
                            getItemLabel={(user) => user.name}
                            getItemKey={(user) => user.uuid}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            <div className="my-t flex grow-0 justify-center">
              <Button
                loading={createFacilityMutation.isPending || isUploading || isSubmitting}
                type="submit"
                className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-12 py-2.5 font-semibold text-white sm:w-auto sm:grow-0"
              >
                <Icon name="label" fill={1} color="#fff" size={16} />
                <span className="[font-family:'Figtree'] text-sm">Salvar estabelecimento</span>
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </BaseLayout>
  );
}
